import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client from "../utils/client";
import {
  Box,
  Button,
  Flex,
  Input,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import UserCard from "../components/UserCard";
import { useAuth } from "../context/AuthContext";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import AdminPage from "../components/AdminPage";

const AllUsers = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const auth = useAuth();

  useEffect(() => {
    const request = async () => {
      const response = await client.get("/users");
      if (response?.data) {
        const ordered = response.data.sort((a, b) =>
          a.name < b.name ? -1 : a.name > b.name ? 1 : 0
        );
        setAllUsers(ordered);
      }
    };
    try {
      request().then(() => setIsLoading(false));
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  }, [auth.user.token]);

  const handleRoleChange = (userId, newType) => {
    const update = (list) =>
      list.map((u) => (u.userId === userId ? { ...u, type: newType } : u));
    setAllUsers((prev) => update(prev));
    setFilteredUsers((prev) => update(prev));
  };

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setFilteredUsers(
      allUsers.filter((el) =>
        el.name.toLowerCase().includes(searchValue.toLowerCase())
      )
    );
  };

  const exportToExcel = () => {
    const usersToExport = filteredUsers.length > 0 ? filteredUsers : allUsers;
    if (usersToExport.length === 0) return;

    const data = usersToExport.map((user) => ({
      Nome: user.name,
      CIM: user.cim,
      Aniversário: user.bithdate,
      Email: user.email,
      Celular: user.phoneNumber,
      Profissão: user.job,
      Loja: user.lodge,
      Obediência: user.obedience,
      Rito: user.rite,
      "Maçom Ativo?": !!user.isActiveMason ? "Ativo" : "Inativo",
      "Grau Simbólico": user.symbolicGrade,
      Endereço: user.address,
      CEP: user.cep,
      UF: user.state,
      Cidade: user.city,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Usuários");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "usuarios.xlsx");
  };

  const displayUsers = filteredUsers.length > 0 ? filteredUsers : allUsers;

  return (
    <Layout>
      <AdminPage
        title="Gerenciar Usuários"
        subtitle={
          filteredUsers.length > 0 && filteredUsers.length !== allUsers.length
            ? `Exibindo ${filteredUsers.length} de ${allUsers.length} membros`
            : `${allUsers.length} ${allUsers.length === 1 ? "membro" : "membros"} cadastrados`
        }
      >
        <Skeleton isLoaded={!isLoading}>
          {/* Toolbar */}
          <Flex
            px={5}
            py={4}
            gap={3}
            flexDir={["column", "row"]}
            borderBottom="1px solid"
            borderColor="gray.100"
            alignItems={["stretch", "center"]}
          >
            <Input
              type="text"
              placeholder="Pesquisar por nome..."
              onChange={handleSearch}
              bg="white"
              borderColor="gray.300"
              maxW={["100%", "320px"]}
              size="sm"
              borderRadius="0.5rem"
            />
            <Button
              colorScheme="green"
              variant="outline"
              size="sm"
              onClick={exportToExcel}
              ml={["0", "auto"]}
            >
              Exportar Excel
            </Button>
          </Flex>

          <TableContainer>
            <Table size="sm" variant="striped" colorScheme="green" fontSize="0.82rem">
              <Thead bg="green.50">
                <Tr>
                  <Th fontSize="0.72rem" maxW="160px">Nome</Th>
                  <Th fontSize="0.72rem">Email</Th>
                  <Th fontSize="0.72rem">Celular</Th>
                  <Th fontSize="0.72rem">Perfil</Th>
                  <Th fontSize="0.72rem">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {displayUsers.map((user) => (
                  <UserCard
                    key={user.userId}
                    user={user}
                    onRoleChange={handleRoleChange}
                  />
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {displayUsers.length === 0 && !isLoading && (
            <Box textAlign="center" py={10} color="gray.400">
              <Text>Nenhum usuário encontrado.</Text>
            </Box>
          )}
        </Skeleton>
      </AdminPage>
    </Layout>
  );
};

export default AllUsers;
